<template>
  <div class="img_center">
    <div v-if="!imageUrls.length" class="product-image-fallback">
      <img
        :src="fallbackImage"
        :alt="productName ? `Kein Produktbild verfügbar: ${productName}` : 'Kein Produktbild verfügbar'"
        loading="lazy"
        decoding="async"
      />
    </div>

    <template v-else>
      <section
        :id="mainSplideId"
        class="splide"
        :aria-label="productName ? `Produktbilder von ${productName}` : 'Produktbilder'"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="(imageUrl, index) in imageUrls"
              :key="mainSplideId + '-image-' + index + '-' + imageUrl"
              class="splide__slide"
            >
              <a
                :href="amazonUrl"
                target="_blank"
                rel="sponsored nofollow noopener"
                :aria-label="productName ? `${productName} auf Amazon ansehen` : 'Produkt auf Amazon ansehen'"
              >
                <img
                  :src="imageUrl"
                  :alt="getProductImageAlt(index)"
                  loading="lazy"
                  decoding="async"
                  @error="handleImageError"
                />
              </a>
            </li>
          </ul>
        </div>
      </section>

      <section
        v-if="imageUrls.length > 1"
        :id="thumbnailSplideId"
        class="splide"
        :aria-label="productName ? `Vorschaubilder von ${productName}` : 'Vorschaubilder'"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="(imageUrl, index) in imageUrls"
              :key="thumbnailSplideId + '-thumb-' + index + '-' + imageUrl"
              class="splide__slide"
            >
              <img
                :src="imageUrl"
                :alt="getProductThumbnailAlt(index)"
                loading="lazy"
                decoding="async"
                @error="handleImageError"
              />
            </li>
          </ul>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
export default {
  name: "PicCarousel",

  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      mainSplide: null,
      thumbnails: null,
      componentUid: Math.random().toString(36).slice(2),
      fallbackImage: "/wp-content/plugins/airfryer-finder/no-image.png",
    };
  },

  computed: {
    productName() {
      return (
        this.product?.amazonNamenSuche ||
        this.product?.amazon_namen_suche ||
        this.product?.titel ||
        this.product?.name ||
        this.product?.produktname ||
        ""
      );
    },

    amazonUrl() {
      return this.product?.amazon_url || "#";
    },

    mainSplideId() {
      return `splide-main-${this.componentUid}`;
    },

    thumbnailSplideId() {
      return `splide-thumb-${this.componentUid}`;
    },

    imageUrls() {
      const urls = [];

      const addUrl = (url) => {
        if (!url) return;

        const cleanUrl = String(url).trim();

        if (!cleanUrl) return;
        if (cleanUrl === "null") return;
        if (cleanUrl === "undefined") return;
        if (!cleanUrl.startsWith("http") && !cleanUrl.startsWith("/")) return;

        urls.push(cleanUrl);
      };

      const addImageString = (value) => {
        if (!value) return;

        if (Array.isArray(value)) {
          value.forEach(addUrl);
          return;
        }

        String(value)
          .split(";")
          .map((url) => url.trim())
          .filter(Boolean)
          .forEach(addUrl);
      };

      addUrl(this.product?.bild_url);
      addUrl(this.product?.bildUrl);
      addUrl(this.product?.image_url);
      addUrl(this.product?.imageUrl);

      addImageString(this.product?.picVariants);
      addImageString(this.product?.pic_variants);
      addImageString(this.product?.pictures);
      addImageString(this.product?.images);

      return [...new Set(urls)];
    },

    imageSignature() {
      return this.imageUrls.join("|");
    },
  },

  watch: {
    imageSignature() {
      this.reInitSplide();
    },

    amazonUrl() {
      this.reInitSplide();
    },
  },

  mounted() {
    this.reInitSplide();
  },

  beforeUnmount() {
    this.destroySplide();
  },

  beforeDestroy() {
    this.destroySplide();
  },

  methods: {
    getProductImageAlt(index) {
      if (this.productName) {
        return index === 0
          ? `Produktbild: ${this.productName}`
          : `Produktbild ${index + 1}: ${this.productName}`;
      }

      return index === 0 ? "Produktbild" : `Produktbild ${index + 1}`;
    },

    getProductThumbnailAlt(index) {
      if (this.productName) {
        return `Vorschaubild ${index + 1}: ${this.productName}`;
      }

      return `Vorschaubild ${index + 1}`;
    },

    reInitSplide() {
      this.destroySplide();

      this.$nextTick(() => {
        this.initSplide();
      });
    },

    initSplide() {
      if (!this.imageUrls.length) {
        return;
      }

      if (typeof Splide === "undefined") {
        console.error("Splide ist nicht geladen.");
        return;
      }

      const mainSplideElement = document.getElementById(this.mainSplideId);
      const thumbnailSplideElement = document.getElementById(this.thumbnailSplideId);

      if (!mainSplideElement) {
        return;
      }

      this.mainSplide = new Splide(mainSplideElement, {
        width: "250px",
        height: "200px",
        type: this.imageUrls.length > 1 ? "fade" : "slide",
        rewind: true,
        pagination: false,
        arrows: false,
      });

      if (this.imageUrls.length > 1 && thumbnailSplideElement) {
        this.thumbnails = new Splide(thumbnailSplideElement, {
          width: "250px",
          fixedWidth: 48,
          fixedHeight: 40,
          gap: 2,
          rewind: true,
          pagination: false,
          isNavigation: true,
          arrows: false,
          breakpoints: {
            1920: {
              width: "250px",
              fixedWidth: 48,
              fixedHeight: 40,
              arrows: false,
            },
            2560: {
              width: "300px",
              fixedWidth: 60,
              fixedHeight: 50,
              arrows: false,
            },
          },
        });

        this.mainSplide.sync(this.thumbnails);
        this.mainSplide.mount();
        this.thumbnails.mount();

        return;
      }

      this.mainSplide.mount();
    },

    destroySplide() {
      if (this.mainSplide) {
        try {
          this.mainSplide.destroy(true);
        } catch (error) {
          console.warn("Main Splide konnte nicht zerstört werden:", error);
        }

        this.mainSplide = null;
      }

      if (this.thumbnails) {
        try {
          this.thumbnails.destroy(true);
        } catch (error) {
          console.warn("Thumbnail Splide konnte nicht zerstört werden:", error);
        }

        this.thumbnails = null;
      }
    },

    handleImageError(event) {
      const img = event.target;

      if (!img || img.dataset.fallbackApplied === "1") {
        return;
      }

      img.dataset.fallbackApplied = "1";
      img.src = this.fallbackImage;
    },
  },
};
</script>

<style scoped>
.img_center {
  text-align: center;
}

.product-image-fallback {
  width: 250px;
  height: 200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image-fallback img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.splide__slide img {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
}
</style>